import * as XLSX from 'xlsx';
import {
  buildColumnIndex,
  buildHeaderIndex,
  EnquiryColumnDef,
  EnquiryImportConfig,
  validateCellFormat,
} from './enquiryColumns';
import { normalizeDateCell, isUnparseableDate } from './dateNormalizer';

/**
 * Parses an uploaded sheet into rows keyed by canonical column key and runs the
 * checks that do not need the database. Anything requiring master data (does
 * this batch exist? is this a duplicate enquiry?) is left to
 * POST /v1/ingestion/validate-enquiry-data.
 */

export interface EnquiryRow {
  [columnKey: string]: string;
}

export interface ValidationError {
  row: number;
  /** Canonical column key — matches the `field` the server returns. */
  field: string;
  message: string;
}

export interface ParseResult {
  rows: EnquiryRow[];
  errors: ValidationError[];
  /** Enabled+mandatory columns whose header is absent from the sheet. */
  missingColumns: string[];
  /** Headers in the file that no enabled column matched. */
  unknownHeaders: string[];
  fileBuffer: string; // base64 of the original file, for the S3 copy
}

const EMPTY_RESULT: ParseResult = {
  rows: [],
  errors: [],
  missingColumns: [],
  unknownHeaders: [],
  fileBuffer: '',
};

export function parseEnquiryExcel(
  buffer: ArrayBuffer,
  columns: EnquiryColumnDef[],
  config: EnquiryImportConfig,
): ParseResult {
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const sheet = sheetName ? wb.Sheets[sheetName] : undefined;
  if (!sheet) return EMPTY_RESULT;

  const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });
  if (jsonData.length === 0) return EMPTY_RESULT;

  const enabled = new Set(config.enabledColumns);
  const enabledColumns = columns.filter(c => enabled.has(c.key));
  const columnIndex = buildColumnIndex(enabledColumns);
  const headerIndex = buildHeaderIndex(enabledColumns);

  // Excel header text -> canonical key (first header wins on a collision).
  const headerKeys = Object.keys(jsonData[0] ?? {});
  const headerMap = new Map<string, string>(); // canonical key -> excel header
  const unknownHeaders: string[] = [];
  for (const header of headerKeys) {
    const key = headerIndex.get(header.trim().toLowerCase());
    if (!key) {
      if (header.trim()) unknownHeaders.push(header.trim());
      continue;
    }
    if (!headerMap.has(key)) headerMap.set(key, header);
  }

  const missingColumns = config.mandatoryColumns
    .filter(key => !headerMap.has(key))
    .map(key => columnIndex.get(key)?.label ?? key);

  const rows: EnquiryRow[] = jsonData.map(raw => {
    const row: EnquiryRow = {};
    for (const column of enabledColumns) {
      const excelKey = headerMap.get(column.key);
      const cell = excelKey ? raw[excelKey] : '';
      if (column.type === 'date') {
        // Excel date cells arrive as serials/Date objects — normalize to
        // YYYY-MM-DD. Anything unreadable is kept as typed so it can be flagged.
        const normalized = normalizeDateCell(cell);
        row[column.key] = normalized || String(cell ?? '').trim();
      } else {
        row[column.key] = excelKey ? String(cell ?? '').trim() : '';
      }
    }
    return row;
  });

  const errors = validateEnquiryRows(rows, enabledColumns, config);
  return { rows, errors, missingColumns, unknownHeaders, fileBuffer: toBase64(buffer) };
}

export function validateEnquiryRows(
  rows: EnquiryRow[],
  enabledColumns: EnquiryColumnDef[],
  config: EnquiryImportConfig,
): ValidationError[] {
  const errors: ValidationError[] = [];
  const columnIndex = buildColumnIndex(enabledColumns);
  const seenIdentity = new Map<string, number>();
  const seenStudentCodes = new Map<string, number>();

  const hasColumn = (key: string) => columnIndex.has(key);

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] ?? {};

    for (const key of config.mandatoryColumns) {
      if (!row[key]) {
        errors.push({ row: i, field: key, message: `${columnIndex.get(key)?.label ?? key} is required` });
      }
    }

    for (const column of enabledColumns) {
      const value = row[column.key] ?? '';
      if (!value) continue;

      if (column.type === 'date') {
        if (isUnparseableDate(value)) {
          errors.push({ row: i, field: column.key, message: 'Invalid date format (use YYYY-MM-DD)' });
        }
        continue;
      }

      const formatError = validateCellFormat(column, value);
      if (formatError) errors.push({ row: i, field: column.key, message: formatError });
    }

    // Explicit student codes must be unique within the file.
    const studentCode = (row['studentcode'] ?? '').trim();
    if (studentCode && studentCode.toUpperCase() !== 'AUTO') {
      const codeKey = studentCode.toLowerCase();
      const previous = seenStudentCodes.get(codeKey);
      if (previous !== undefined) {
        errors.push({
          row: i,
          field: 'studentcode',
          message: `Duplicate StudentCode "${studentCode}" (same as row ${previous + 2})`,
        });
      } else {
        seenStudentCodes.set(codeKey, i);
      }
    }

    // Same person twice in one file is almost always a copy/paste mistake.
    if (hasColumn('firstname') && hasColumn('lastname')) {
      const firstName = (row['firstname'] ?? '').toLowerCase();
      const lastName = (row['lastname'] ?? '').toLowerCase();
      const mobile = row['mobile'] ?? '';
      if (firstName && lastName) {
        const identity = `${firstName}_${lastName}_${mobile}`;
        const previous = seenIdentity.get(identity);
        if (previous !== undefined) {
          errors.push({
            row: i,
            field: 'firstname',
            message: `Possible duplicate (same name+mobile as row ${previous + 2})`,
          });
        } else {
          seenIdentity.set(identity, i);
        }
      }
    }
  }

  return errors;
}

export function getRowErrors(errors: ValidationError[], rowIndex: number): ValidationError[] {
  return errors.filter(e => e.row === rowIndex);
}

export function rowHasErrors(errors: ValidationError[], rowIndex: number): boolean {
  return errors.some(e => e.row === rowIndex);
}

function toBase64(buffer: ArrayBuffer): string {
  const uint8 = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 0x8000; // avoid blowing the argument limit on large files
  for (let i = 0; i < uint8.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(uint8.subarray(i, i + CHUNK)) as any);
  }
  return btoa(binary);
}
